/**
 * CPL 레인 AR · 1단계 「선결」 — 현장 상세의 «파생값 한 벌» (2026-09-23).
 *
 * ── 왜 이 파일이 먼저인가 ──
 * `/apt/[id]/page.tsx` 는 같은 판정을 여러 자리에서 «각자» 다시 짰다. 그 상태로 섹션을
 * 쪼개면 복제가 파일 경계를 넘어 흩어지고, 그다음부터는 「어느 쪽이 정본인가」를
 * 아무도 답할 수 없게 된다. 그래서 쪼개기 «전에» 파생을 한 곳으로 모은다.
 *
 * ── 이 파일이 지키는 계약 ──
 * ⛔ 값을 «고치지 않는다». 각 함수는 종전 인라인 식과 «문자 그대로» 같은 결과를 낸다.
 *    (레인 AR 의 동일성 게이트: 렌더 HTML 이 정규화 후 바이트 동일)
 * ⛔ 여기에 DB 조회·JSX·`new Date()` 를 두지 않는다. 순수 파생만 둔다 —
 *    「오늘」은 렌더 진입점이 «한 번» 찍어서 인자로 넘긴다(page.tsx 의 todayKST 규율).
 * ⚠️ 새 판정을 «만들지» 않는다. 없던 정규화·폴백을 여기서 인심 쓰듯 더하면
 *    그 순간 동일성 게이트가 깨지고, 깨진 이유를 찾는 데 하루가 간다.
 */

import { eokShort } from '@/lib/fmt';
import { canUseHeroImage } from '@/lib/apt/hero-license';
import { salePeriodDisplay, type SalePeriodDisplay } from '@/lib/apt/sale-period';

/* ────────────────────────────────────────────────────────────────────────────
 * 0. 입력 모양
 *
 * ⚠️ `apt_sites` 행은 이 레포에서 `as any` 로 다뤄진다(생성 타입이 뷰·후속 컬럼을
 *    다 담지 않는다). 파생 계약만 좁게 적고 나머지는 열어 둔다 — 여기서 타입을
 *    조이면 호출부가 캐스팅을 더 쌓을 뿐 안전해지지 않는다.
 * ──────────────────────────────────────────────────────────────────────────── */

/** 파생이 읽는 `apt_sites` 행의 «부분» 모양. */
export interface DerivedSiteLike {
  lifecycle_stage?: string | null;
  hero_license_tier?: string | null;
  hero_image_url?: string | null;
  hero_image_source?: string | null;
  satellite_image_url?: string | null;
  site_type?: string | null;
  expected_sale_period?: string | null;
  expected_sale_source?: string | null;
  expected_sale_period_asof?: string | null;
  confidence?: string | null;
  source_ids?: Record<string, string> | null;
  [k: string]: unknown;
}

/** 파생이 읽는 `apt_subscriptions` 행의 «부분» 모양. */
export interface DerivedSubLike {
  rcept_bgnde?: string | null;
  rcept_endde?: string | null;
  developer_nm?: string | null;
  [k: string]: unknown;
}

/* ────────────────────────────────────────────────────────────────────────────
 * 1. 표기 도우미 — page.tsx 에 있던 것을 «그대로» 옮겼다.
 * ──────────────────────────────────────────────────────────────────────────── */

/** 만원 단위 금액 표기. ⚠️ 0·null 은 `-` 다(종전 동작 — falsy 검사였다). */
export function fmtAmount(n: number | null) { return eokShort(n) ?? '-'; } // 정본 lib/fmt(버림)

/** `YYYYMM` → `YYYY년 M월`. 값이 없으면 null. */
export function fmtYM(s: string | null) { if (!s) return null; return `${s.slice(0, 4)}년 ${parseInt(s.slice(4, 6))}월`; }

/**
 * CPL A2 — `YYYYMMDD` → `YYYY년 M월 D일`. 판독 불가면 **null**.
 *
 * ⛔ 청약홈 날짜 컬럼(`przwner_presnatn_de`·`cntrct_cncls_bgnde`·`rcept_bgnde` …)은
 *    8자리 «원시» 문자열이다. 그대로 문장에 넣으면 「발표일 20250711」이 나간다 —
 *    사람이 읽는 날짜가 아니고, 그것이 구조화 데이터로 가면 기계도 날짜로 읽지 못한다.
 * ⛔ 모르면 «지어내지 않는다». 8자리가 아니거나 달·일이 범위를 벗어나면 null 을 내고,
 *    호출부는 그 문장을 통째로 뺀다(「미정」이라 쓰지 않는다 — A2 규율).
 * ⚠️ 실재 날짜인지까지 보지는 않는다(2월 31일은 통과). 형식·범위 검사다.
 */
export function fmtYMD(s: string | null | undefined): string | null {
  const v = (s ?? '').trim();
  if (!/^\d{8}$/.test(v)) return null;
  const y = Number(v.slice(0, 4)), m = Number(v.slice(4, 6)), d = Number(v.slice(6, 8));
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return `${y}년 ${m}월 ${d}일`;
}

/**
 * s-v2 2번 블록 — 한 줄 요약.
 * analysis_text 는 채움률 100% 라 어느 현장을 열어도 이 블록이 비지 않는다.
 * HTML 이므로 태그를 걷어내고 앞 n문장만 쓴다 (전문은 8번 접힘 블록에 그대로 남는다).
 */
export function firstSentences(html: string | null, n: number): string | null {
  if (!html) return null;
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return null;
  // 마침표/물음표/느낌표 뒤 공백을 문장 경계로 본다. 소수점(3.3㎡)은 뒤에 공백이 없어 안 걸린다.
  const parts = text.split(/(?<=[.!?])\s+/).filter(Boolean);
  const out = parts.slice(0, n).join(' ').trim();
  return out.length >= 20 ? out : null;
}

/* ────────────────────────────────────────────────────────────────────────────
 * 2. 배지 어휘 — 화면 여러 곳이 같은 표를 읽는다.
 *   ⛔ 여기 적힌 토큰을 컴포넌트에서 다시 적지 말 것. 갈리면 같은 배지가 두 색이 된다.
 * ──────────────────────────────────────────────────────────────────────────── */

export const tLabel: Record<string, string> = { subscription: '분양', redevelopment: '재개발', unsold: '미분양', landmark: '랜드마크', complex: '기존단지', trade: '실거래' };
// s7-5: 다크 전제 하드코딩 → 기존 accent 토큰. 흰 배경 합성 실측으로 전부 4.5:1 이상.
// (이전 값은 1.38~2.39:1 — 알파를 합성하면 배경과 거의 구분되지 않았다. Rule #85/#86)
export const tBg: Record<string, string> = { subscription: 'var(--accent-green-bg)', redevelopment: 'var(--accent-purple-bg)', unsold: 'var(--accent-red-bg)', landmark: 'var(--accent-blue-bg)', complex: 'var(--accent-blue-bg)', trade: 'var(--accent-yellow-bg)' };
export const tClr: Record<string, string> = { subscription: 'var(--accent-green)', redevelopment: 'var(--accent-purple)', unsold: 'var(--accent-red)', landmark: 'var(--accent-cyan)', complex: 'var(--accent-cyan)', trade: 'var(--accent-yellow)' };

/* ⛔ CPL A-5 (2026-09-23) — 여기 있던 `STAGES`(정비 6칸)를 «걷었다».
 *    DB 원문(`'추진위원회 구성'`·`'예정구역'` 등 20종)을 짧은 라벨로 대조하다 칸이 비었고,
 *    같은 병을 D2 가 `/apt/redev/*` 에서 고치며 별칭 정본을 세웠다.
 *    정본은 `lib/apt/redev-stage.ts` 의 `REDEV_STAGE_ORDER` + `normalizeRedevStage` 다.
 *    유일 소비처였던 `sections/RedevSection.tsx` 가 그쪽을 읽는다. */

export type SubStatus = 'open' | 'upcoming' | 'closed';

/** 접수 상태 배지의 색·문구 한 벌. */
export const SB: Record<string, { label: string; bg: string; color: string; border: string }> = {
  open: { label: '접수중', bg: 'var(--accent-green-bg)', color: 'var(--accent-green)', border: 'var(--accent-green)' },
  upcoming: { label: '접수예정', bg: 'var(--accent-yellow-bg)', color: 'var(--accent-yellow)', border: 'var(--accent-yellow)' },
  closed: { label: '마감', bg: 'transparent', color: 'var(--text-tertiary)', border: 'var(--border)' },
};

/* ────────────────────────────────────────────────────────────────────────────
 * 3. 중복 판정 — 「같은 질문을 여러 자리에서 각자 답하던」 다섯.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * ① 기축(준공·입주 완료) 계열인가.
 *
 * 종전 page.tsx 에서 «여섯 자리» 가 이 식을 각자 적고 있었다:
 *   generateMetadata 의 `isBuilt`·`isGichukMeta` · 본문 `isGichukLd`·`isExistingComplex`
 *   · AB-2 조립 FAQ 의 `built` · AB-1 개요의 `built`.
 * 여섯이 같은 값이어야 한다는 사실이 코드 어디에도 적혀 있지 않았다 — 여기 적는다.
 *
 * ⚠️ `lib/apt/title-tail.ts` 의 `isBuiltLike` 와 «다른 집합» 이다.
 *    그쪽은 `move_in_started` 를 포함하고 site_type 도 본다(제목 꼬리용).
 *    이쪽은 화면·이미지 체인용 둘뿐이다. 합치지 말 것 — 합치면 입주 «시작» 현장의
 *    히어로가 위성으로 바뀐다.
 */
export function isGichuk(lifecycleStage: string | null | undefined): boolean {
  return lifecycleStage === 'post_move_in' || lifecycleStage === 'landmark_active';
}

/**
 * ② 이 현장의 히어로 실사를 이 화면에서 써도 되는가.
 *
 * 종전 «세 자리» 가 `canUseHeroImage({tier, lifecycleStage})` 를 각자 불렀다
 * (og:image · JSON-LD image[] · 히어로 블록). 셋이 갈리면 화면에서 뺀 이미지가
 * 공유 카드나 검색 결과에 그대로 나타난다 — 그래서 한 벌이어야 한다.
 */
/**
 * W4-3 (2026-09-27) — 상세 히어로 정책. `app_config(images.allow_satellite_hero)` 를 렌더 진입점이 한 번 읽어 넘긴다
 * (`lib/apt/hero-policy.ts getHeroPolicy`). 이 파일은 DB 를 부르지 않는다는 계약 그대로 — 값은 인자로만 온다.
 * ⚠️ 인자를 안 넘기면(undefined) 종전 동작과 «바이트 동일» 이다. 카드 호출부(licensedHeroPhoto)는 넘기지 않는다.
 */
export interface HeroPolicy {
  /** false 면 위성 히어로(hero_image_source='satellite' 사진 · 기축 satellite_image_url 폴백)를 뺀다. 기본 true. */
  allowSatelliteHero?: boolean;
}

function satelliteBlocked(site: DerivedSiteLike | null | undefined, policy?: HeroPolicy): boolean {
  return policy?.allowSatelliteHero === false
    && String(site?.hero_image_source ?? '').trim().toLowerCase() === 'satellite';
}

export function heroLicenseOk(site: DerivedSiteLike | null | undefined, policy?: HeroPolicy): boolean {
  /* U3 (2026-09-27, 즉시 집행) — 상세 히어로 사진은 `hero_image_license` 가 «적힌» 것만.
   * 실측: hero_image_url 182건 중 179건에 라이선스 문장이 있고 3건(위성 2·시행사 1)이 비어 있다.
   * 비어 있으면 히어로·og:image·JSON-LD 가 모두 생성 카드 체인으로 떨어진다(세 자리가 이 함수 하나를 탄다).
   * ⚠️ 열을 SELECT 에 싣지 않은 호출부(undefined)도 «없음» 으로 본다 — 모르는 것을 통과시키지 않는다. */
  if (!String((site as any)?.hero_image_license ?? '').trim()) return false;
  // W4-3 — allow_satellite_hero=false 면 위성 출처 히어로는 라이선스가 있어도 뺀다(배포 없이 끄기).
  if (satelliteBlocked(site, policy)) return false;
  return canUseHeroImage({
    tier: site?.hero_license_tier,
    lifecycleStage: site?.lifecycle_stage,
  });
}

/**
 * ③ 분양예정 시기 한 줄(U-1a §7-1).
 *
 * 종전 «두 자리» 가 같은 네 컬럼을 각자 꺼내 `salePeriodDisplay` 에 넘겼다
 * (메타 인용형 description · 히어로 캡션). 컬럼 이름을 한쪽만 고치는 날 갈린다.
 * ⛔ 판정 자체는 `lib/apt/sale-period.ts` 가 한다(테스트로 잠겨 있다 · RULES#143).
 *    여기는 «인자를 모으는» 자리일 뿐이다.
 */
export function salePeriodOf(site: DerivedSiteLike | null | undefined): SalePeriodDisplay | null {
  return salePeriodDisplay({
    period: (site as any)?.expected_sale_period,
    source: (site as any)?.expected_sale_source,
    asof: (site as any)?.expected_sale_period_asof,
    confidence: (site as any)?.confidence,
  });
}

/**
 * ④ 개요 문장이 부르는 이름.
 *
 * 종전 «두 자리» 가 `String(dn ?? name).split(' — ')[0]` 를 각자 적었다
 * (메타 인용형 · AB-1 개요 섹션). 꼬리 구분자(` — `)를 한쪽만 바꾸면 갈린다.
 * ⚠️ `buildSiteOverview` 호출 «자체» 는 합치지 않았다 — 두 호출은 인자가 다르다
 *    (메타는 schedule 을 sp 한 줄로 짜 넣고 maxFloor 를 안 넘긴다). 억지로 합치면
 *    둘 중 한쪽의 출력이 바뀐다. 겹치는 것은 이 이름 한 줄뿐이라 이것만 모은다.
 */
export function overviewLeadName(displayName: string | null | undefined, name: string): string {
  return String(displayName ?? name).split(' — ')[0];
}

/**
 * ⑤ 평당가(만원).
 *
 * CPL A-3 (2026-09-23) — **산식을 `lib/apt/supply-price.ts` 로 옮겼다.**
 *   평당가를 적는 자리가 상세 밖에도 있었다(크론 둘 · 단지백과 · 지역 · 실거래 API).
 *   여기만 한 벌이면 「상세 안에서만」 한 벌이고, 상류가 다른 식을 쓰면 화면과 DB 가 갈린다.
 *   그래서 정본은 `supply-price.ts` 한 파일이고 이 이름은 **재수출** 로만 남긴다
 *   (종전 호출부 `NoticeSummary` 를 깨지 않기 위해서다 — 동작은 바이트 그대로다).
 * ⚠️ 넘기는 면적이 전용인지 공급인지는 부르는 쪽 책임이다. 분양가는 **공급면적** 기준이고,
 *    현장 단위 단일값은 `supplyPyeongPrice()` 가 낸다.
 */
export { pyeongPrice, PYEONG_SQM } from './supply-price';

/* ────────────────────────────────────────────────────────────────────────────
 * 4. 배지 파생 — 화면에 배지·칩으로 나가는 일곱.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * ⑴ 현장 계열. `tLabel`·`tBg`·`tClr` 의 키다.
 * ⚠️ 본문과 `generateMetadata` 의 폴백 사슬이 «다르다»(본문은 마지막이 'subscription',
 *    메타는 'redevelopment'). 실제로 다르므로 한 함수로 합치지 않고 본문 것만 옮긴다 —
 *    합치는 순간 한쪽 출력이 바뀐다.
 */
export function siteTypeOf(
  site: { site_type?: string | null } | null | undefined,
  sub: unknown, unsold: unknown, redev: unknown, tradeCount: number,
): string {
  return site?.site_type || (sub ? 'subscription' : unsold ? 'unsold' : redev ? 'redevelopment' : tradeCount > 0 ? 'trade' : 'subscription');
}

/**
 * ⑵ 청약 접수 상태.
 * ⚠️ `today` 를 인자로 받는다 — 렌더 안에서 「오늘」을 다시 찍지 않기 위해서다.
 *    한 화면에서 기준이 갈리면 배지와 D-day 가 서로 다른 날을 말한다.
 */
export function subStatusOf(sub: DerivedSubLike | null | undefined, today: string): SubStatus | null {
  return sub ? (!sub.rcept_bgnde ? 'upcoming' : today >= sub.rcept_bgnde && today <= (sub.rcept_endde as string) ? 'open' : today < sub.rcept_bgnde ? 'upcoming' : 'closed') : null;
}

/** ⑶ 접수 마감까지 남은 일수. 지났으면 음수다(호출부가 `D+n` 으로 표기한다). */
export function dDayOf(sub: DerivedSubLike | null | undefined, today: string): number | null {
  return sub?.rcept_endde ? Math.ceil((new Date(sub.rcept_endde).getTime() - new Date(today).getTime()) / 86400000) : null;
}

/**
 * ⑷ 정비 단계 배지 문구.
 * ⚠️ `source_ids.redev_stage` 가 `redev.stage` 보다 앞선다(종전 순서 그대로).
 */
export function redevStageOf(
  site: { source_ids?: Record<string, string> | null } | null | undefined,
  redev: { stage?: string | null } | null | undefined,
): string | null | undefined {
  return (site?.source_ids as Record<string, string>)?.redev_stage || redev?.stage;
}

/** ⑸ 분양가 등급 배지(만원 단위 평균가 기준). */
export function priceTier(avgManwon: number): { label: string; color: string } {
  return avgManwon >= 120000 ? { label: '12억+', color: 'var(--accent-red)' }
    : avgManwon >= 90000 ? { label: '9억대', color: 'var(--accent-orange)' }
    : avgManwon >= 60000 ? { label: '6억대', color: 'var(--accent-yellow)' }
    : avgManwon >= 30000 ? { label: '3억대', color: 'var(--brand)' }
    : { label: '3억 미만', color: 'var(--accent-green)' };
}

/**
 * ⑹ 시행 유형 배지. 시행사 «이름 문자열» 로 판정한다.
 * ⚠️ 이것은 추론이지 DB 값이 아니다 — 판정 못 하면 null 이고 배지를 내지 않는다.
 */
export function devTypeOf(developerName: string): { label: string; color: string } | null {
  const isJohap = developerName.includes('조합') || developerName.includes('정비');
  const isPublic = developerName.includes('공사') || developerName.includes('LH') || developerName.includes('SH');
  return isJohap ? { label: '재개발/재건축 조합', color: 'var(--accent-orange)' } : isPublic ? { label: '공공 시행', color: 'var(--accent-green)' } : null;
}

/**
 * ⑺ 히어로 이미지 갈래 — ADDENDUM §6-4 우선순위.
 *   1순위 실사(hero_image_url · 라이선스 통과분)  2순위 기축이면 위성  3순위 생성 카드
 * ⚠️ 기축인데 위성이 «없는» 24건은 카드로 떨어진다([패치 P1 §4]). 'none' 으로 두면
 *    목록(카드)과 상세(텍스트 폴백)가 갈린다.
 */
/**
 * Node 12:25 판정(2026-09-27) — «라이선스 열이 채워진 사진» 한 장. 히어로와 카드가 «같은 함수» 로 고른다(두 벌 금지).
 * 판정은 heroLicenseOk 하나(hero_image_license 비어 있지 않음 + tier 게이트). 통과 못 하면 ''.
 * ⚠️ satellite_image_url 에는 출처·라이선스 기록 열이 없다(apt_sites 실측: hero_image_* 열뿐) — 여기서 쓰지 않는다.
 *    위성이 라이선스를 달고 오는 유일한 길은 hero_image_url(hero_image_source='satellite') + hero_image_license 이다.
 * ⛔ images jsonb·cover_image_url(크롤)은 이 함수의 입력이 아니다.
 */
export function licensedHeroPhoto(site: DerivedSiteLike | null | undefined, policy?: HeroPolicy): string {
  if (!heroLicenseOk(site, policy)) return '';
  return String(site?.hero_image_url ?? '').trim();
}

export function heroKindOf(site: DerivedSiteLike | null | undefined, policy?: HeroPolicy): 'developer' | 'satellite' | 'card' | 'none' {
  const devHeroSrc = heroLicenseOk(site, policy) ? ((site?.hero_image_url as string | undefined) || '') : '';
  // W4-3 — 위성 끄기면 기축 위성 폴백도 카드로 떨어진다(OG·JSON-LD 도 같은 정책을 탄다 — page.tsx).
  const satSrc = policy?.allowSatelliteHero === false ? '' : ((site?.satellite_image_url as string | undefined) || '');
  return devHeroSrc ? 'developer'
    : isGichuk(site?.lifecycle_stage) ? (satSrc ? 'satellite' : 'card')
    : 'card';
}

/* ────────────────────────────────────────────────────────────────────────────
 * 5. 단계 — CPL A-5 (2026-09-23).
 *
 * ⛔ 판정부는 `lib/apt/title-tail.ts` 한 곳이다. **여기는 재수출뿐이다.**
 *    제목 꼬리와 본문 배치가 «같은 표» 를 읽어야 하고, 그 표가 사는 곳은 꼬리 쪽이다
 *    (꼬리표가 곧 단계군의 원본이었다 — 그 파일 §CPL A-5 머리말 참조).
 * ⚠️ 상세가 쓰는 파생은 이 파일에서 받는다는 관례를 깨지 않으려고 이름만 통과시킨다.
 *    여기에 «다른 기본값·다른 폴백» 을 끼워 넣지 말 것 — 그 순간 두 벌이 된다.
 * ⚠️ `isGichuk`(위 ①)과 `deriveStage(...)==='moved_in'` 은 **다른 집합** 이다.
 *    isGichuk 은 post_move_in·landmark_active 둘(이미지 체인용)이고,
 *    'moved_in' 은 거기에 move_in_started 를 더한 «제목이 실거래를 약속하는 군» 이다.
 *    합치지 말 것 — 합치면 입주 «시작» 현장의 히어로가 위성으로 바뀐다.
 * ──────────────────────────────────────────────────────────────────────────── */
export { deriveStage, stageGroupOf, isBuiltLike, BUILT_STAGES } from './title-tail';
export type { DetailStage, DeriveStageInput } from './title-tail';

/** 부가 — 미분양률(%). 공급세대가 없으면 null 이고 화면이 `-` 로 낸다. */
export function unsoldRateOf(unsold: { tot_supply_hshld_co?: number | null; tot_unsold_hshld_co?: number | null } | null | undefined): number | null {
  return unsold?.tot_supply_hshld_co ? Math.round(((unsold.tot_unsold_hshld_co ?? 0) / unsold.tot_supply_hshld_co) * 100) : null;
}
